import { Box, Typography } from '@mui/material'
import { Countries } from '@xoda/common'
import { t } from 'i18next'
import { useConfirm } from 'material-ui-confirm'
import { useEffect, useMemo, useRef } from 'react'
import toast from 'react-hot-toast'
import { useRecoilState } from 'recoil'
import { Api } from '../api'
import {
  getGoodsType,
  getPlanPrice,
  GoodsItem,
  isPack,
  isPlan,
  isProduct,
} from '../components/Goods'
import { Modal } from '../components/Modal'
import {
  PaymentMethodForm,
  PaymentMethodFormProps,
} from '../components/PaymentMethodForm'
import { gymState } from '../store/gym'
import { useGet } from './get'
import { useSwitch } from './switch'
import { useTc } from './tc'
import { useUser } from './user'

const usePurchase = (item?: GoodsItem['dto']) => {
  const confirm = useConfirm()

  const { user } = useUser()
  const [gym] = useRecoilState(gymState)
  const paymentMethodForm = useSwitch()
  const pending = useSwitch()
  const paymentMethodPending = useSwitch()

  const resolvePromise =
    useRef<Parameters<ConstructorParameters<typeof Promise>[0]>[0]>()

  const stripeParams = useMemo(() => {
    if (!user) {
      return
    }

    return new URLSearchParams({
      lead_id: String(user.leadId),
      ...(item && {
        [`${getGoodsType(item)}_id`]: String(item.id),
      }),
    })
  }, [user, item])

  const { data: checkout } = useGet<Dto.Checkout>(() => {
    if (!(paymentMethodForm.on && user?.leadId)) {
      return
    }

    return [
      'member_portal/checkout',
      {
        lead_id: user.leadId,
        success_url: String(
          new URL(`/stripe?${stripeParams || ''}`, window.location.origin)
        ),
        cancel_url: String(new URL('/classes', window.location.origin)),
      },
    ]
  })

  const { tcElement, paymentTcElement, formTc } = useTc(item, checkout)

  const submit = (extraPayload?: Parameters<typeof request>[0]) =>
    new Promise(async (resolve, reject) => {
      try {
        if (!item) {
          return
        }

        pending.open()
        const price = isPlan(item) ? getPlanPrice(item).number : item.price
        const promoCode = extraPayload?.promo_code

        if (price) {
          const discounts = promoCode
            ? await Api.post('promo_code/prices', {
                code: promoCode,
                items: [
                  {
                    type: getGoodsType(item),
                    id: item.id,
                    price,
                  },
                ],
              })
            : undefined
          const finalPrice = discounts?.[0].discounted_price ?? price

          const { confirmed } = await confirm({
            title: t('Checkout'),
            content: (
              <>
                <Box mb={2}>
                  <Typography variant="h3">{item.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {item.description}
                  </Typography>
                </Box>
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    fontSize: 16,

                    '& tfoot': {
                      fontWeight: 600,
                    },
                    '& th, & td': {
                      padding: '0.25em 1em',
                    },
                    '& tbody th': {
                      fontWeight: 'initial',
                    },
                    '& th': {
                      textAlign: 'start',
                    },
                    '& td': {
                      textAlign: 'end',
                    },
                  }}
                >
                  <tbody>
                    <tr>
                      <th scope="row">{t('Amount')}</th>
                      <td>
                        {Countries.formatAmount(
                          price,
                          gym?.currency_code,
                          true
                        )}
                      </td>
                    </tr>
                    {finalPrice < price && (
                      <tr>
                        <th scope="row">{t('Promo code')}</th>
                        <td>
                          {Countries.formatAmount(
                            finalPrice - price,
                            gym?.currency_code,
                            true
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row">{t('Total')}</th>
                      <td>
                        {Countries.formatAmount(
                          finalPrice,
                          gym?.currency_code,
                          true
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </Box>
              </>
            ),
            confirmationText: t('Continue'),
            cancellationText: t('Cancel'),
          })

          if (!confirmed) {
            return
          }
        }

        await request(extraPayload)
        resolve(undefined)
      } catch (error) {
        if (
          error instanceof Error &&
          error.message.includes('payment method has not yet been added')
        ) {
          resolvePromise.current = resolve
          toast.loading(error.message)
          paymentMethodForm.open()
        } else {
          reject(error)
        }
      } finally {
        pending.close()
      }
    })

  const request = async (extraPayload?: Record<string, unknown>) => {
    if (!(user && gym && item)) {
      return
    }

    await Api.post(
      'purchases',
      {
        gym_id: user.gymId,
        user_id: user.userId,
        payment_method: (item.gym || gym).payment_method,
        pos_items: isProduct(item)
          ? [
              {
                type: 'product',
                type_id: item.id,
                name: item.name,
                quantity: 1,
                price: item.price,
              },
            ]
          : [],
        ...(isPlan(item) && {
          gym_plan_id: item.id,
          gym_plan_payment_schedule: item.payment_schedule || 'weekly',
          gym_plan_price: false,
        }),
        ...(isPack(item) && {
          gym_packs: [{ id: item.id, price: false }],
        }),
        ...(formTc && {
          term_form_id: formTc.id,
          version: formTc.version,
        }),
        ...extraPayload,
      },
      { memberAuth: true }
    )
  }

  const handleSucceed: PaymentMethodFormProps['onSucceed'] = async (result) => {
    try {
      await Api.put('member_portal/members/payment_method', result, {
        memberAuth: true,
      })
      await request()
      paymentMethodForm.close()
      resolvePromise.current?.(undefined)
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      }
    } finally {
      paymentMethodPending.close()
    }
  }

  const handleError = (error: unknown) => {
    paymentMethodPending.close()
    toast.error(String(error))
  }

  const paymentMethodElement = user && checkout && (
    <Modal open={paymentMethodForm.on} onClose={paymentMethodForm.close}>
      <PaymentMethodForm
        lastName={user.lastName}
        payment={checkout.payment}
        pending={paymentMethodPending.on}
        tcElement={paymentTcElement}
        onSubmit={paymentMethodPending.open}
        onSucceed={handleSucceed}
        onError={handleError}
      />
    </Modal>
  )

  useEffect(() => {
    if (!!checkout) {
      toast.dismiss()
    }
  }, [checkout])

  return {
    submit,
    paymentMethodElement,
    tcElement,
    pending: pending.on,
  }
}

export { usePurchase }
